export type PolicyStatus = 'Active' | 'Draft' | 'Inactive';
export type ProtectionLevel = 'Standard' | 'Strict' | 'Maximum';
export type PolicyType =
  | 'Prompt Security'
  | 'Data Protection'
  | 'Access Control'
  | 'Runtime Enforcement'
  | 'Compliance'
  | 'Tool Governance'
  | 'Model Governance';
export type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';
export type AuditDecision = 'Allow' | 'Mask' | 'Deny' | 'Approval Required';

export interface PolicyRow {
  id: string;
  name: string;
  code: string;
  description: string;
  type: PolicyType;
  group: string;
  protectionLevel: ProtectionLevel;
  status: PolicyStatus;
  assignedAssets: number;
  lastUpdated: string;
  lastUpdatedTime?: string;
  riskLevel: RiskLevel;
}

export interface PolicyGroupRow {
  id: string;
  name: string;
  policyCount: number;
  assignedAssets: number;
  riskLevel: RiskLevel;
  owner: string;
  lastUpdated: string;
  description: string;
}

export interface AssignmentRow {
  id: string;
  resourceName: string;
  resourceType: string;
  department: string;
  assignedPolicy: string;
  inheritanceSource: 'Enterprise Default' | 'Department Default' | 'Resource Override';
  overrideStatus: 'None' | 'Active Override' | 'Expired';
  lastEvaluated: string;
}

export interface OverrideRow {
  id: string;
  scope: string;
  scopeType: 'User' | 'Group' | 'Department' | 'Resource' | 'Workflow' | 'Knowledge Base';
  policy: string;
  exceptionType: string;
  reason: string;
  approvedBy: string;
  expiryDate: string;
  status: 'Active' | 'Expired' | 'Revoked';
  riskLevel: RiskLevel;
}

export interface AuditLogRow {
  id: string;
  timestamp: string;
  user: string;
  resource: string;
  policy: string;
  decision: AuditDecision;
  riskLevel: RiskLevel;
  toolInvoked: string;
  modelUsed: string;
  details: string;
}

export interface PolicyFormState {
  name: string;
  code: string;
  description: string;
  status: PolicyStatus;
  group: string;
  protectionLevel: ProtectionLevel;
  appliesTo: string[];
  promptFirewall: {
    enabled: boolean;
    blockPromptInjection: boolean;
    blockJailbreak: boolean;
    detectHiddenInstructions: boolean;
    detectPromptLeakage: boolean;
    detectCredentialExtraction: boolean;
    detectUnsafeSql: boolean;
    detectMalwareRequests: boolean;
    detectDataExfiltration: boolean;
    riskActions: Record<string, string>;
  };
  dataProtection: {
    classification: string;
    sensitiveTypes: string[];
    maskSensitiveData: boolean;
    redactPii: boolean;
    preventBulkExport: boolean;
    requireCitations: boolean;
    blockExternalModel: boolean;
    encryptStoredContext: boolean;
    enableRetentionRules: boolean;
  };
  resourceGovernance: {
    allowedLlms: string[];
    allowedKnowledgeBases: string[];
    allowedVectorStores: string[];
    allowedMcpTools: string[];
    allowedWorkflows: string[];
    allowedChatbots: string[];
    allowedDataConnections: string[];
  };
  authConditions: Array<{
    id: string;
    field: string;
    operator: string;
    value: string;
    action: string;
  }>;
  approvalRules: {
    highRiskPrompts: boolean;
    bulkExport: boolean;
    restrictedDataAccess: boolean;
    externalModelUsage: boolean;
    financialThreshold: boolean;
    approverRole: string;
    expirationTime: string;
    escalationRule: string;
  };
  overrides: {
    allowTemporary: boolean;
    scopes: string[];
    reason: string;
    approver: string;
    expiryDate: string;
    riskLevel: RiskLevel;
  };
  audit: {
    enabled: boolean;
    logPrompts: boolean;
    logResponses: boolean;
    logPolicyDecisions: boolean;
    logToolCalls: boolean;
    logRetrievedDocuments: boolean;
    logBlockedRequests: boolean;
    complianceMappings: string[];
  };
  outputValidation: {
    scanBeforeSend: boolean;
    maskPiiInResponse: boolean;
    preventPromptLeakage: boolean;
    preventConfidentialLeakage: boolean;
    detectToxicContent: boolean;
    requireSourceCitations: boolean;
    blockOnViolation: boolean;
  };
}

export const POLICY_TYPES: PolicyType[] = [
  'Prompt Security',
  'Data Protection',
  'Access Control',
  'Runtime Enforcement',
  'Compliance',
  'Tool Governance',
  'Model Governance',
];

export const SAMPLE_POLICIES: PolicyRow[] = [
  { id: '1', name: 'Financial Data Protection', code: 'financial_strict_v1', description: 'Protect financial and banking data', type: 'Data Protection', group: 'Finance', protectionLevel: 'Maximum', status: 'Active', assignedAssets: 12, lastUpdated: '2025-06-15', lastUpdatedTime: '10:30 AM', riskLevel: 'High' },
  { id: '2', name: 'HR Confidentiality', code: 'hr_strict_v1', description: 'Employee personal data handling', type: 'Access Control', group: 'HR', protectionLevel: 'Strict', status: 'Active', assignedAssets: 8, lastUpdated: '2025-06-14', lastUpdatedTime: '09:15 AM', riskLevel: 'High' },
  { id: '3', name: 'Model Usage Control', code: 'model_usage_v1', description: 'Restrict AI model access and usage', type: 'Model Governance', group: 'Platform', protectionLevel: 'Standard', status: 'Draft', assignedAssets: 4, lastUpdated: '2025-06-13', lastUpdatedTime: '04:45 PM', riskLevel: 'Medium' },
  { id: '4', name: 'R&D Data Access', code: 'rd_data_v1', description: 'Research data classification rules', type: 'Access Control', group: 'R&D', protectionLevel: 'Strict', status: 'Active', assignedAssets: 6, lastUpdated: '2025-06-12', lastUpdatedTime: '11:20 AM', riskLevel: 'Medium' },
  { id: '5', name: 'HIPAA Compliance', code: 'healthcare_hipaa_v1', description: 'Healthcare data protection standards', type: 'Compliance', group: 'Healthcare', protectionLevel: 'Maximum', status: 'Active', assignedAssets: 15, lastUpdated: '2025-06-11', lastUpdatedTime: '02:00 PM', riskLevel: 'Critical' },
  { id: '6', name: 'EU AI Act', code: 'eu_ai_act_v1', description: 'European AI regulations compliance', type: 'Compliance', group: 'Legal', protectionLevel: 'Maximum', status: 'Active', assignedAssets: 22, lastUpdated: '2025-06-10', lastUpdatedTime: '08:30 AM', riskLevel: 'High' },
  { id: '7', name: 'Content Safety', code: 'content_safety_v1', description: 'Filter harmful or inappropriate outputs', type: 'Prompt Security', group: 'Platform', protectionLevel: 'Strict', status: 'Active', assignedAssets: 18, lastUpdated: '2025-06-09', lastUpdatedTime: '03:15 PM', riskLevel: 'Medium' },
  { id: '8', name: 'Research Data Policy', code: 'research_data_v1', description: 'Academic research data governance', type: 'Data Protection', group: 'R&D', protectionLevel: 'Standard', status: 'Inactive', assignedAssets: 3, lastUpdated: '2025-06-08', lastUpdatedTime: '01:45 PM', riskLevel: 'Low' },
  { id: '9', name: 'Legal Confidential Policy', code: 'legal_confidential_v1', description: 'Legal privilege and confidentiality controls', type: 'Compliance', group: 'Legal', protectionLevel: 'Maximum', status: 'Active', assignedAssets: 9, lastUpdated: '2025-06-07', lastUpdatedTime: '10:00 AM', riskLevel: 'Critical' },
  { id: '10', name: 'Developer Sandbox Policy', code: 'dev_sandbox_v1', description: 'Relaxed controls for dev environments', type: 'Tool Governance', group: 'Engineering', protectionLevel: 'Standard', status: 'Active', assignedAssets: 14, lastUpdated: '2025-06-06', lastUpdatedTime: '05:30 PM', riskLevel: 'Low' },
  { id: '11', name: 'Customer Support Policy', code: 'customer_support_v1', description: 'Customer-facing chatbot governance', type: 'Prompt Security', group: 'Support', protectionLevel: 'Strict', status: 'Draft', assignedAssets: 5, lastUpdated: '2025-06-05', lastUpdatedTime: '09:45 AM', riskLevel: 'Medium' },
  { id: '12', name: 'Enterprise Default Policy', code: 'enterprise_default_v1', description: 'Baseline enterprise-wide defaults', type: 'Runtime Enforcement', group: 'Platform', protectionLevel: 'Standard', status: 'Active', assignedAssets: 56, lastUpdated: '2025-06-04', lastUpdatedTime: '12:00 PM', riskLevel: 'Medium' },
];

export const RECENT_POLICY_ACTIVITY = [
  { id: '1', type: 'Policy Updated', description: 'Financial Data Protection rules modified', time: '10:30 AM', dot: 'bg-blue-500' },
  { id: '2', type: 'Policy Assigned', description: 'HIPAA Compliance assigned to 3 new assets', time: '09:15 AM', dot: 'bg-emerald-500' },
  { id: '3', type: 'Override Created', description: 'Temporary exception for R&D team', time: 'Yesterday', dot: 'bg-amber-500' },
  { id: '4', type: 'Policy Created', description: 'New Content Safety policy drafted', time: 'Yesterday', dot: 'bg-violet-500' },
  { id: '5', type: 'Policy Deactivated', description: 'Research Data Policy set to inactive', time: '2 days ago', dot: 'bg-slate-400' },
];

export const SAMPLE_GROUPS: PolicyGroupRow[] = [
  { id: '1', name: 'Finance', policyCount: 4, assignedAssets: 24, riskLevel: 'High', owner: 'Sarah Chen', lastUpdated: '2025-06-18', description: 'Financial data protection and SOX compliance policies.' },
  { id: '2', name: 'Human Resources', policyCount: 3, assignedAssets: 18, riskLevel: 'High', owner: 'Marcus Webb', lastUpdated: '2025-06-15', description: 'Employee data and HR workflow governance.' },
  { id: '3', name: 'Legal', policyCount: 2, assignedAssets: 12, riskLevel: 'Critical', owner: 'Elena Rodriguez', lastUpdated: '2025-06-12', description: 'Legal confidentiality and privilege protection.' },
  { id: '4', name: 'Healthcare', policyCount: 3, assignedAssets: 31, riskLevel: 'Critical', owner: 'Dr. James Park', lastUpdated: '2025-06-10', description: 'HIPAA and PHI handling policies.' },
  { id: '5', name: 'Platform Default', policyCount: 1, assignedAssets: 156, riskLevel: 'Medium', owner: 'Platform Admin', lastUpdated: '2025-06-20', description: 'Baseline enterprise-wide AI governance defaults.' },
  { id: '6', name: 'Customer Support', policyCount: 2, assignedAssets: 8, riskLevel: 'Medium', owner: 'Lisa Thompson', lastUpdated: '2025-06-22', description: 'Customer-facing chatbot and support agent policies.' },
  { id: '7', name: 'Developer Sandbox', policyCount: 2, assignedAssets: 42, riskLevel: 'Low', owner: 'Alex Kumar', lastUpdated: '2025-06-08', description: 'Relaxed policies for development and testing environments.' },
];

export const SAMPLE_ASSIGNMENTS: AssignmentRow[] = [
  { id: '1', resourceName: 'Finance Copilot', resourceType: 'Chatbot', department: 'Finance', assignedPolicy: 'Financial Strict Policy', inheritanceSource: 'Department Default', overrideStatus: 'None', lastEvaluated: '2025-06-28 09:14' },
  { id: '2', resourceName: 'HR Knowledge Base', resourceType: 'Knowledge Base', department: 'Human Resources', assignedPolicy: 'HR Strict Policy', inheritanceSource: 'Department Default', overrideStatus: 'None', lastEvaluated: '2025-06-28 08:52' },
  { id: '3', resourceName: 'Contract Review Workflow', resourceType: 'Workflow', department: 'Legal', assignedPolicy: 'Legal Confidential Policy', inheritanceSource: 'Resource Override', overrideStatus: 'Active Override', lastEvaluated: '2025-06-28 10:01' },
  { id: '4', resourceName: 'Patient Intake Agent', resourceType: 'Chatbot', department: 'Healthcare', assignedPolicy: 'Healthcare HIPAA Policy', inheritanceSource: 'Department Default', overrideStatus: 'None', lastEvaluated: '2025-06-28 07:30' },
  { id: '5', resourceName: 'GPT-4o Production', resourceType: 'LLM', department: 'Platform', assignedPolicy: 'Enterprise Default Policy', inheritanceSource: 'Enterprise Default', overrideStatus: 'None', lastEvaluated: '2025-06-28 11:22' },
  { id: '6', resourceName: 'Salesforce Connector', resourceType: 'Data Connection', department: 'Sales', assignedPolicy: 'Enterprise Default Policy', inheritanceSource: 'Enterprise Default', overrideStatus: 'None', lastEvaluated: '2025-06-27 16:45' },
  { id: '7', resourceName: 'Dev Sandbox LLM', resourceType: 'LLM', department: 'Engineering', assignedPolicy: 'Developer Sandbox Policy', inheritanceSource: 'Resource Override', overrideStatus: 'None', lastEvaluated: '2025-06-28 06:18' },
];

export const SAMPLE_OVERRIDES: OverrideRow[] = [
  { id: '1', scope: 'j.smith@acme.com', scopeType: 'User', policy: 'Financial Strict Policy', exceptionType: 'Temporary Access', reason: 'Quarter-end audit data export', approvedBy: 'Sarah Chen', expiryDate: '2025-07-05', status: 'Active', riskLevel: 'High' },
  { id: '2', scope: 'Legal Review Team', scopeType: 'Group', policy: 'Legal Confidential Policy', exceptionType: 'Tool Allowlist', reason: 'External counsel document review', approvedBy: 'Elena Rodriguez', expiryDate: '2025-07-15', status: 'Active', riskLevel: 'Critical' },
  { id: '3', scope: 'Contract Review Workflow', scopeType: 'Workflow', policy: 'Enterprise Default Policy', exceptionType: 'Model Override', reason: 'Testing Claude for legal summarization', approvedBy: 'Platform Admin', expiryDate: '2025-06-30', status: 'Active', riskLevel: 'Medium' },
  { id: '4', scope: 'Engineering Dept', scopeType: 'Department', policy: 'Developer Sandbox Policy', exceptionType: 'Data Classification', reason: 'Internal hackathon project', approvedBy: 'Alex Kumar', expiryDate: '2025-06-01', status: 'Expired', riskLevel: 'Low' },
];

export const SAMPLE_AUDIT_LOGS: AuditLogRow[] = [
  { id: '1', timestamp: '2025-06-28 11:22:14', user: 'j.smith@acme.com', resource: 'Finance Copilot', policy: 'Financial Strict Policy', decision: 'Mask', riskLevel: 'High', toolInvoked: 'sql_query', modelUsed: 'GPT-4o', details: 'PII detected in prompt — masked SSN fields before processing.' },
  { id: '2', timestamp: '2025-06-28 11:18:03', user: 'm.webb@acme.com', resource: 'HR Knowledge Base', policy: 'HR Strict Policy', decision: 'Allow', riskLevel: 'Low', toolInvoked: 'document_search', modelUsed: 'GPT-4o-mini', details: 'Standard HR policy lookup — no sensitive data exposure.' },
  { id: '3', timestamp: '2025-06-28 11:05:47', user: 'unknown@external.com', resource: 'Customer Support Bot', policy: 'Customer Support Policy', decision: 'Deny', riskLevel: 'Critical', toolInvoked: '—', modelUsed: 'GPT-4o', details: 'Prompt injection attempt blocked — jailbreak pattern detected.' },
  { id: '4', timestamp: '2025-06-28 10:58:22', user: 'e.rodriguez@acme.com', resource: 'Contract Review Workflow', policy: 'Legal Confidential Policy', decision: 'Approval Required', riskLevel: 'High', toolInvoked: 'external_api', modelUsed: 'Claude 3.5', details: 'External model usage for confidential document — awaiting approval.' },
  { id: '5', timestamp: '2025-06-28 10:42:11', user: 'a.kumar@acme.com', resource: 'Dev Sandbox LLM', policy: 'Developer Sandbox Policy', decision: 'Allow', riskLevel: 'Low', toolInvoked: 'code_executor', modelUsed: 'GPT-4o-mini', details: 'Sandbox environment — unrestricted development testing.' },
  { id: '6', timestamp: '2025-06-28 10:30:55', user: 'd.park@acme.com', resource: 'Patient Intake Agent', policy: 'Healthcare HIPAA Policy', decision: 'Deny', riskLevel: 'Critical', toolInvoked: 'bulk_export', modelUsed: 'GPT-4o', details: 'Bulk PHI export blocked — retention policy violation.' },
];

export const DEFAULT_POLICY_FORM: PolicyFormState = {
  name: '',
  code: '',
  description: '',
  status: 'Draft',
  group: 'Platform Default',
  protectionLevel: 'Standard',
  appliesTo: ['Chatbots', 'Workflows'],
  promptFirewall: {
    enabled: true,
    blockPromptInjection: true,
    blockJailbreak: true,
    detectHiddenInstructions: true,
    detectPromptLeakage: true,
    detectCredentialExtraction: true,
    detectUnsafeSql: true,
    detectMalwareRequests: true,
    detectDataExfiltration: true,
    riskActions: {
      Safe: 'Allow',
      Low: 'Allow + Audit',
      Medium: 'Ask Confirmation',
      High: 'Block + Audit',
      Critical: 'Block + Notify Security Admin',
    },
  },
  dataProtection: {
    classification: 'Internal',
    sensitiveTypes: ['PII'],
    maskSensitiveData: true,
    redactPii: true,
    preventBulkExport: true,
    requireCitations: false,
    blockExternalModel: false,
    encryptStoredContext: true,
    enableRetentionRules: true,
  },
  resourceGovernance: {
    allowedLlms: ['GPT-4o', 'GPT-4o-mini', 'Claude 3.5'],
    allowedKnowledgeBases: [],
    allowedVectorStores: [],
    allowedMcpTools: [],
    allowedWorkflows: [],
    allowedChatbots: [],
    allowedDataConnections: [],
  },
  authConditions: [
    { id: '1', field: 'user.department', operator: 'equals', value: 'Finance', action: 'allow' },
  ],
  approvalRules: {
    highRiskPrompts: true,
    bulkExport: true,
    restrictedDataAccess: true,
    externalModelUsage: true,
    financialThreshold: true,
    approverRole: 'Security Admin',
    expirationTime: '24 hours',
    escalationRule: 'Escalate to CISO after 4 hours',
  },
  overrides: {
    allowTemporary: true,
    scopes: ['User', 'Group'],
    reason: '',
    approver: '',
    expiryDate: '',
    riskLevel: 'Medium',
  },
  audit: {
    enabled: true,
    logPrompts: true,
    logResponses: true,
    logPolicyDecisions: true,
    logToolCalls: true,
    logRetrievedDocuments: true,
    logBlockedRequests: true,
    complianceMappings: ['GDPR', 'SOC 2'],
  },
  outputValidation: {
    scanBeforeSend: true,
    maskPiiInResponse: true,
    preventPromptLeakage: true,
    preventConfidentialLeakage: true,
    detectToxicContent: true,
    requireSourceCitations: false,
    blockOnViolation: true,
  },
};

export function buildPolicyJson(form: PolicyFormState) {
  return {
    policy_id: form.code || 'new_policy',
    name: form.name,
    protection_level: form.protectionLevel.toLowerCase(),
    status: form.status.toLowerCase(),
    applies_to: form.appliesTo,
    prompt_firewall: form.promptFirewall,
    data_protection: form.dataProtection,
    resource_governance: form.resourceGovernance,
    authorization_conditions: form.authConditions,
    approval_rules: form.approvalRules,
    overrides: form.overrides,
    audit: form.audit,
    output_validation: form.outputValidation,
  };
}
